import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { 
    ArrowLeft,
    User,
    Phone,
    Mail,
    MapPin,
    Calendar,
    Briefcase,
    Heart,
    AlertTriangle,
    Shirt,
    Save,
    Plus,
    Trash2,
    Edit2,
    FileText,
    DollarSign,
    Clock,
    Shield,
    Camera,
    Upload,
    X
} from 'lucide-react';
import { PhoneInput, formatPhoneForDisplay } from '../components/ui/phone-input';

const BLOOD_TYPES = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
const GENDERS = [
    { value: 'Homme', label: 'Homme' },
    { value: 'Femme', label: 'Femme' },
    { value: 'Autre', label: 'Autre' }
];
const ID_TYPES = ['CNI', 'Passeport', 'Carte de séjour', 'Permis de conduire'];
const UNIFORM_SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];
const RELATIONS = ['Père', 'Mère', 'Conjoint(e)', 'Frère', 'Soeur', 'Enfant', 'Ami(e)', 'Autre'];

export default function StaffDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { api, user, hasPermission, formatCurrency } = useAuth();
    
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [staff, setStaff] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [formData, setFormData] = useState({});
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const fileInputRef = useRef(null);
    
    // Custom field dialog
    const [showCustomFieldDialog, setShowCustomFieldDialog] = useState(false);
    const [newCustomField, setNewCustomField] = useState({ field_name: '', field_value: '', field_type: 'text' });
    
    const isAdmin = user?.role === 'ADMIN';
    const canEdit = hasPermission ? hasPermission('edit_staff') : false;

    useEffect(() => {
        fetchStaffDetail();
    }, [id]);

    const fetchStaffDetail = async () => {
        try {
            const response = await api().get(`/staff/${id}`);
            setStaff(response.data);
            setFormData(response.data);
        } catch (error) {
            console.error('Error fetching staff:', error);
            toast.error('Erreur lors du chargement');
            navigate('/staff');
        } finally {
            setLoading(false);
        }
    };

    const handlePhotoUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        
        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            toast.error('La photo ne doit pas dépasser 5 Mo');
            return;
        }
        
        setUploadingPhoto(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            
            const response = await api().post(`/staff/${id}/photo`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            
            toast.success('Photo uploadée avec succès');
            fetchStaffDetail(); // Reload to get new photo URL
        } catch (error) {
            console.error('Error uploading photo:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload');
        } finally {
            setUploadingPhoto(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleDeletePhoto = async () => {
        if (!window.confirm('Supprimer la photo de profil ?')) return;
        
        try {
            await api().delete(`/staff/${id}/photo`);
            toast.success('Photo supprimée');
            fetchStaffDetail();
        } catch (error) {
            toast.error('Erreur lors de la suppression');
        }
    };

    const getPhotoUrl = (url) => {
        if (!url) return null;
        // If URL starts with /api, prepend the backend URL
        if (url.startsWith('/api')) {
            const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
            return `${backendUrl}${url}`;
        }
        return url;
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await api().put(`/staff/${id}`, formData);
            toast.success('Profil mis à jour');
            setEditMode(false);
            fetchStaffDetail();
        } catch (error) {
            console.error('Error updating staff:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
        } finally {
            setSaving(false);
        }
    };

    const handleAddCustomField = async () => {
        if (!newCustomField.field_name) {
            toast.error('Nom du champ requis');
            return;
        }
        try {
            await api().post(`/staff/${id}/custom-field`, newCustomField);
            toast.success('Champ ajouté');
            setShowCustomFieldDialog(false);
            setNewCustomField({ field_name: '', field_value: '', field_type: 'text' });
            fetchStaffDetail();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        }
    };

    const handleRemoveCustomField = async (fieldName) => {
        if (!window.confirm(`Supprimer le champ "${fieldName}" ?`)) return;
        try {
            await api().delete(`/staff/${id}/custom-field/${fieldName}`);
            toast.success('Champ supprimé');
            fetchStaffDetail();
        } catch (error) {
            toast.error('Erreur lors de la suppression');
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('fr-FR');
    };

    const calculateAge = (birthDate) => {
        if (!birthDate) return null;
        const today = new Date();
        const birth = new Date(birthDate);
        let age = today.getFullYear() - birth.getFullYear();
        const m = today.getMonth() - birth.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
            age--;
        }
        return age;
    };

    const calculateSeniority = (hireDate) => {
        if (!hireDate) return null;
        const today = new Date();
        const hire = new Date(hireDate);
        const months = (today.getFullYear() - hire.getFullYear()) * 12 + (today.getMonth() - hire.getMonth());
        if (months < 12) return `${months} mois`;
        const years = Math.floor(months / 12);
        const remainingMonths = months % 12;
        return remainingMonths > 0 ? `${years} an(s) et ${remainingMonths} mois` : `${years} an(s)`;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (!staff) {
        return (
            <div className="text-center py-12">
                <p className="text-muted-foreground">Employé non trouvé</p>
                <Button variant="outline" className="mt-4" onClick={() => navigate('/staff')}>
                    Retour à la liste
                </Button>
            </div>
        );
    }

    const getRoleBadgeColor = (role) => {
        const colors = {
            'ADMIN': 'bg-red-600',
            'DIRECTEUR': 'bg-amber-500',
            'DIRECTEUR_ADJOINT': 'bg-blue-500',
            'CHEF_CUISINE': 'bg-green-500',
            'CUISINIER': 'bg-orange-500',
            'SERVEUR': 'bg-purple-500',
            'EMPLOYE': 'bg-gray-500'
        };
        return colors[role] || 'bg-gray-500';
    };

    return (
        <div className="space-y-6">
            {/* Hidden file input for photo upload */}
            <input 
                type="file" 
                ref={fileInputRef}
                className="hidden"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={handlePhotoUpload}
            />
            
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" onClick={() => navigate('/staff')}>
                        <ArrowLeft className="w-5 h-5" />
                    </Button>
                    <div className="flex items-center gap-4">
                        {/* Photo with upload option */}
                        <div className="relative group">
                            <div className="w-20 h-20 rounded-full bg-accent flex items-center justify-center overflow-hidden border-4 border-background shadow-lg">
                                {staff.photo_url ? (
                                    <img 
                                        src={getPhotoUrl(staff.photo_url)} 
                                        alt={staff.full_name} 
                                        className="w-full h-full object-cover" 
                                    />
                                ) : (
                                    <User className="w-10 h-10 text-muted-foreground" />
                                )}
                            </div>
                            {/* Upload overlay - visible on hover if canEdit */}
                            {canEdit && (
                                <div className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                    {uploadingPhoto ? (
                                        <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent"></div>
                                    ) : (
                                        <div className="flex gap-1">
                                            <button 
                                                className="p-1.5 bg-white/20 rounded-full hover:bg-white/30 transition-colors"
                                                onClick={() => fileInputRef.current?.click()}
                                                title="Changer la photo"
                                            >
                                                <Camera className="w-4 h-4 text-white" />
                                            </button>
                                            {staff.photo_url && (
                                                <button 
                                                    className="p-1.5 bg-red-500/50 rounded-full hover:bg-red-500/70 transition-colors"
                                                    onClick={handleDeletePhoto}
                                                    title="Supprimer la photo"
                                                >
                                                    <X className="w-4 h-4 text-white" />
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold">{staff.full_name}</h1>
                            <div className="flex items-center gap-2 mt-1">
                                <Badge className={getRoleBadgeColor(staff.role)}>{staff.position}</Badge>
                                <span className="text-muted-foreground">•</span>
                                <span className="text-muted-foreground">{staff.service}</span>
                                {!staff.is_active && (
                                    <Badge variant="destructive">Inactif</Badge>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                {canEdit && (
                    <div className="flex gap-2">
                        {editMode ? (
                            <>
                                <Button variant="outline" onClick={() => { setEditMode(false); setFormData(staff); }}>
                                    Annuler
                                </Button>
                                <Button onClick={handleSave} disabled={saving}>
                                    <Save className="w-4 h-4 mr-2" />
                                    {saving ? 'Enregistrement...' : 'Enregistrer'}
                                </Button>
                            </>
                        ) : (
                            <Button onClick={() => setEditMode(true)}>
                                <Edit2 className="w-4 h-4 mr-2" />
                                Modifier
                            </Button>
                        )}
                    </div>
                )}
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <Calendar className="w-5 h-5 text-blue-500" />
                            <div>
                                <p className="text-xs text-muted-foreground">Âge</p>
                                <p className="font-semibold">{calculateAge(staff.date_of_birth) || '-'} ans</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <Briefcase className="w-5 h-5 text-green-500" />
                            <div>
                                <p className="text-xs text-muted-foreground">Ancienneté</p>
                                <p className="font-semibold">{calculateSeniority(staff.hire_date) || '-'}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <Heart className="w-5 h-5 text-red-500" />
                            <div>
                                <p className="text-xs text-muted-foreground">Groupe sanguin</p>
                                <p className="font-semibold">{staff.blood_type || '-'}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <Shield className="w-5 h-5 text-emerald-500" />
                            <div>
                                <p className="text-xs text-muted-foreground">Vaccins</p>
                                <p className={`font-semibold ${staff.vaccines_up_to_date ? 'text-green-600' : 'text-red-600'}`}>
                                    {staff.vaccines_up_to_date ? 'À jour' : 'Non à jour'}
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Tabs */}
            <Tabs defaultValue="identity" className="w-full">
                <TabsList className="grid w-full grid-cols-5">
                    <TabsTrigger value="identity">Identité</TabsTrigger>
                    <TabsTrigger value="employment">Emploi</TabsTrigger>
                    <TabsTrigger value="health">Santé</TabsTrigger>
                    <TabsTrigger value="emergency">Urgence</TabsTrigger>
                    {isAdmin && <TabsTrigger value="custom">Personnalisé</TabsTrigger>}
                </TabsList>

                {/* Identity Tab */}
                <TabsContent value="identity">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <User className="w-5 h-5" />
                                Informations personnelles
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-4">
                                <div>
                                    <Label>Prénom</Label>
                                    {editMode ? (
                                        <Input 
                                            value={formData.first_name || ''} 
                                            onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                                        />
                                    ) : (
                                        <p className="text-lg">{staff.first_name || '-'}</p>
                                    )}
                                </div>
                                <div>
                                    <Label>Nom</Label>
                                    {editMode ? (
                                        <Input 
                                            value={formData.last_name || ''} 
                                            onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                                        />
                                    ) : (
                                        <p className="text-lg">{staff.last_name || '-'}</p>
                                    )}
                                </div>
                                <div>
                                    <Label>Genre</Label>
                                    {editMode ? (
                                        <Select value={formData.gender || ''} onValueChange={(v) => setFormData({...formData, gender: v})}>
                                            <SelectTrigger><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                                            <SelectContent>
                                                {GENDERS.map(g => (
                                                    <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    ) : (
                                        <p className="text-lg">{staff.gender || '-'}</p>
                                    )}
                                </div>
                                <div>
                                    <Label>Date de naissance</Label>
                                    {editMode ? (
                                        <Input 
                                            type="date" 
                                            value={formData.date_of_birth || ''} 
                                            onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})}
                                        />
                                    ) : (
                                        <p className="text-lg">{formatDate(staff.date_of_birth)}</p>
                                    )}
                                </div>
                                <div>
                                    <Label>Nationalité</Label>
                                    {editMode ? (
                                        <Input 
                                            value={formData.nationality || ''} 
                                            onChange={(e) => setFormData({...formData, nationality: e.target.value})}
                                        />
                                    ) : (
                                        <p className="text-lg">{staff.nationality || '-'}</p>
                                    )}
                                </div>
                            </div>
                            <div className="space-y-4">
                                <div>
                                    <Label className="flex items-center gap-2"><Mail className="w-4 h-4" /> Email</Label>
                                    {editMode ? (
                                        <Input 
                                            type="email"
                                            value={formData.email || ''} 
                                            onChange={(e) => setFormData({...formData, email: e.target.value})}
                                        />
                                    ) : (
                                        <p className="text-lg">{staff.email || '-'}</p>
                                    )}
                                </div>
                                <div>
                                    <Label className="flex items-center gap-2"><Phone className="w-4 h-4" /> Téléphone</Label>
                                    {editMode ? (
                                        <PhoneInput 
                                            value={formData.phone || ''} 
                                            onChange={(v) => setFormData({...formData, phone: v})}
                                        />
                                    ) : (
                                        <p className="text-lg">{formatPhoneForDisplay(staff.phone) || '-'}</p>
                                    )}
                                </div>
                                <div>
                                    <Label className="flex items-center gap-2"><MapPin className="w-4 h-4" /> Adresse</Label>
                                    {editMode ? (
                                        <Textarea 
                                            value={formData.address || ''} 
                                            onChange={(e) => setFormData({...formData, address: e.target.value})}
                                            rows={3}
                                        />
                                    ) : (
                                        <p className="text-lg whitespace-pre-line">{staff.address || '-'}</p>
                                    )}
                                </div>
                                <div>
                                    <Label className="flex items-center gap-2"><FileText className="w-4 h-4" /> Pièce d'identité</Label>
                                    {editMode ? (
                                        <div className="flex gap-2">
                                            <Select value={formData.id_type || ''} onValueChange={(v) => setFormData({...formData, id_type: v})}>
                                                <SelectTrigger className="w-1/3"><SelectValue placeholder="Type" /></SelectTrigger>
                                                <SelectContent>
                                                    {ID_TYPES.map(t => (
                                                        <SelectItem key={t} value={t}>{t}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            <Input 
                                                className="flex-1"
                                                placeholder="Numéro"
                                                value={formData.id_number || ''} 
                                                onChange={(e) => setFormData({...formData, id_number: e.target.value})}
                                            />
                                        </div>
                                    ) : (
                                        <p className="text-lg">{staff.id_type ? `${staff.id_type}: ${staff.id_number || '-'}` : '-'}</p>
                                    )}
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Employment Tab */}
                <TabsContent value="employment">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Briefcase className="w-5 h-5" />
                                Informations professionnelles
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-4">
                                <div>
                                    <Label>Service</Label>
                                    <p className="text-lg">{staff.service}</p>
                                </div>
                                <div>
                                    <Label>Poste</Label>
                                    <p className="text-lg">{staff.position}</p>
                                </div>
                                <div>
                                    <Label>Qualification</Label>
                                    {editMode ? (
                                        <Textarea 
                                            value={formData.qualification || ''} 
                                            onChange={(e) => setFormData({...formData, qualification: e.target.value})}
                                            placeholder="Diplômes, certifications, expériences..."
                                            rows={3}
                                        />
                                    ) : (
                                        <p className="text-lg whitespace-pre-line">{staff.qualification || '-'}</p>
                                    )}
                                </div>
                                <div>
                                    <Label>Date d'embauche</Label>
                                    {editMode ? (
                                        <Input 
                                            type="date" 
                                            value={formData.hire_date || ''} 
                                            onChange={(e) => setFormData({...formData, hire_date: e.target.value})}
                                        />
                                    ) : (
                                        <p className="text-lg">{formatDate(staff.hire_date)}</p>
                                    )}
                                </div>
                            </div>
                            <div className="space-y-4">
                                <div>
                                    <Label className="flex items-center gap-2"><DollarSign className="w-4 h-4" /> Type de salaire</Label>
                                    <p className="text-lg">{staff.salary_type === 'MONTHLY' ? 'Mensuel' : 'Horaire'}</p>
                                </div>
                                {isAdmin && (
                                    <>
                                        <div>
                                            <Label>Salaire mensuel</Label>
                                            {editMode ? (
                                                <Input 
                                                    type="number" 
                                                    value={formData.monthly_salary || 0} 
                                                    onChange={(e) => setFormData({...formData, monthly_salary: parseFloat(e.target.value)})}
                                                />
                                            ) : (
                                                <p className="text-lg font-semibold text-green-600">
                                                    {formatCurrency ? formatCurrency(staff.monthly_salary) : `${staff.monthly_salary} CFA`}
                                                </p>
                                            )}
                                        </div>
                                        <div>
                                            <Label>Taux horaire</Label>
                                            {editMode ? (
                                                <Input 
                                                    type="number" 
                                                    value={formData.hourly_rate || 0} 
                                                    onChange={(e) => setFormData({...formData, hourly_rate: parseFloat(e.target.value)})}
                                                />
                                            ) : (
                                                <p className="text-lg">
                                                    {formatCurrency ? formatCurrency(staff.hourly_rate) : `${staff.hourly_rate} CFA`}/h
                                                </p>
                                            )}
                                        </div>
                                    </>
                                )}
                                <div>
                                    <Label className="flex items-center gap-2"><Shirt className="w-4 h-4" /> Uniforme</Label>
                                    {editMode ? (
                                        <div className="flex gap-2">
                                            <Select value={formData.uniform_size || ''} onValueChange={(v) => setFormData({...formData, uniform_size: v})}>
                                                <SelectTrigger className="w-1/2"><SelectValue placeholder="Taille" /></SelectTrigger>
                                                <SelectContent>
                                                    {UNIFORM_SIZES.map(s => (
                                                        <SelectItem key={s} value={s}>{s}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            <Input 
                                                className="w-1/2"
                                                placeholder="Pointure"
                                                value={formData.shoe_size || ''} 
                                                onChange={(e) => setFormData({...formData, shoe_size: e.target.value})}
                                            />
                                        </div>
                                    ) : (
                                        <p className="text-lg">
                                            Taille: {staff.uniform_size || '-'} | Pointure: {staff.shoe_size || '-'}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Health Tab */}
                <TabsContent value="health">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Heart className="w-5 h-5" />
                                Informations de santé
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-4">
                                <div>
                                    <Label>Groupe sanguin</Label>
                                    {editMode ? (
                                        <Select value={formData.blood_type || ''} onValueChange={(v) => setFormData({...formData, blood_type: v})}>
                                            <SelectTrigger><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                                            <SelectContent>
                                                {BLOOD_TYPES.map(bt => (
                                                    <SelectItem key={bt} value={bt}>{bt}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    ) : (
                                        <p className="text-lg font-semibold text-red-600">{staff.blood_type || '-'}</p>
                                    )}
                                </div>
                                <div>
                                    <Label>Vaccins à jour</Label>
                                    {editMode ? (
                                        <Select 
                                            value={formData.vaccines_up_to_date ? 'true' : 'false'} 
                                            onValueChange={(v) => setFormData({...formData, vaccines_up_to_date: v === 'true'})}
                                        >
                                            <SelectTrigger><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="true">Oui</SelectItem>
                                                <SelectItem value="false">Non</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    ) : (
                                        <Badge className={staff.vaccines_up_to_date ? 'bg-green-500' : 'bg-red-500'}>
                                            {staff.vaccines_up_to_date ? 'Oui' : 'Non'}
                                        </Badge>
                                    )}
                                </div>
                            </div>
                            <div className="space-y-4">
                                <div>
                                    <Label>Allergies connues</Label>
                                    {editMode ? (
                                        <Textarea 
                                            value={formData.allergies || ''} 
                                            onChange={(e) => setFormData({...formData, allergies: e.target.value})}
                                            placeholder="Liste des allergies..."
                                            rows={3}
                                        />
                                    ) : (
                                        <p className={`text-lg ${staff.allergies ? 'text-orange-600' : ''}`}>
                                            {staff.allergies || 'Aucune allergie connue'}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Emergency Tab */}
                <TabsContent value="emergency">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2 text-red-600">
                                <AlertTriangle className="w-5 h-5" />
                                Contact d'urgence
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-4">
                                <div>
                                    <Label>Nom du contact</Label>
                                    {editMode ? (
                                        <Input 
                                            value={formData.emergency_contact_name || ''} 
                                            onChange={(e) => setFormData({...formData, emergency_contact_name: e.target.value})}
                                        />
                                    ) : (
                                        <p className="text-lg font-semibold">{staff.emergency_contact_name || '-'}</p>
                                    )}
                                </div>
                                <div>
                                    <Label>Relation</Label>
                                    {editMode ? (
                                        <Select value={formData.emergency_contact_relation || ''} onValueChange={(v) => setFormData({...formData, emergency_contact_relation: v})}>
                                            <SelectTrigger><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                                            <SelectContent>
                                                {RELATIONS.map(r => (
                                                    <SelectItem key={r} value={r}>{r}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    ) : (
                                        <p className="text-lg">{staff.emergency_contact_relation || '-'}</p>
                                    )}
                                </div>
                            </div>
                            <div className="space-y-4">
                                <div>
                                    <Label className="flex items-center gap-2"><Phone className="w-4 h-4" /> Téléphone d'urgence</Label>
                                    {editMode ? (
                                        <PhoneInput 
                                            value={formData.emergency_contact_phone || ''} 
                                            onChange={(v) => setFormData({...formData, emergency_contact_phone: v})}
                                        />
                                    ) : (
                                        <p className="text-lg font-semibold text-red-600">
                                            {formatPhoneForDisplay(staff.emergency_contact_phone) || '-'}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Custom Fields Tab (Admin only) */}
                {isAdmin && (
                    <TabsContent value="custom">
                        <Card>
                            <CardHeader className="flex flex-row items-center justify-between">
                                <CardTitle className="flex items-center gap-2">
                                    <Plus className="w-5 h-5" />
                                    Champs personnalisés
                                </CardTitle>
                                <Button size="sm" onClick={() => setShowCustomFieldDialog(true)}>
                                    <Plus className="w-4 h-4 mr-2" />
                                    Ajouter un champ
                                </Button>
                            </CardHeader>
                            <CardContent>
                                {staff.custom_fields && Object.keys(staff.custom_fields).length > 0 ? (
                                    <div className="space-y-4">
                                        {Object.entries(staff.custom_fields).map(([key, field]) => (
                                            <div key={key} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                                                <div>
                                                    <p className="font-medium">{key}</p>
                                                    <p className="text-lg">{field.value || '-'}</p>
                                                    <p className="text-xs text-muted-foreground">
                                                        Ajouté par {field.added_by} le {formatDate(field.added_at)}
                                                    </p>
                                                </div>
                                                <Button 
                                                    variant="ghost" 
                                                    size="icon"
                                                    className="text-red-500 hover:text-red-700"
                                                    onClick={() => handleRemoveCustomField(key)}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-center text-muted-foreground py-8">
                                        Aucun champ personnalisé. Cliquez sur "Ajouter un champ" pour en créer.
                                    </p>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>
                )}
            </Tabs>

            {/* Custom Field Dialog */}
            <Dialog open={showCustomFieldDialog} onOpenChange={setShowCustomFieldDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Ajouter un champ personnalisé</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label>Nom du champ</Label>
                            <Input 
                                value={newCustomField.field_name}
                                onChange={(e) => setNewCustomField({...newCustomField, field_name: e.target.value})}
                                placeholder="Ex: Permis de conduire"
                            />
                        </div>
                        <div>
                            <Label>Type</Label>
                            <Select value={newCustomField.field_type} onValueChange={(v) => setNewCustomField({...newCustomField, field_type: v})}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="text">Texte</SelectItem>
                                    <SelectItem value="number">Nombre</SelectItem>
                                    <SelectItem value="date">Date</SelectItem>
                                    <SelectItem value="boolean">Oui/Non</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Valeur</Label>
                            {newCustomField.field_type === 'boolean' ? (
                                <Select value={newCustomField.field_value} onValueChange={(v) => setNewCustomField({...newCustomField, field_value: v})}>
                                    <SelectTrigger><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="Oui">Oui</SelectItem>
                                        <SelectItem value="Non">Non</SelectItem>
                                    </SelectContent>
                                </Select>
                            ) : newCustomField.field_type === 'date' ? (
                                <Input 
                                    type="date"
                                    value={newCustomField.field_value}
                                    onChange={(e) => setNewCustomField({...newCustomField, field_value: e.target.value})}
                                />
                            ) : newCustomField.field_type === 'number' ? (
                                <Input 
                                    type="number"
                                    value={newCustomField.field_value}
                                    onChange={(e) => setNewCustomField({...newCustomField, field_value: e.target.value})}
                                />
                            ) : (
                                <Input 
                                    value={newCustomField.field_value}
                                    onChange={(e) => setNewCustomField({...newCustomField, field_value: e.target.value})}
                                />
                            )}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowCustomFieldDialog(false)}>Annuler</Button>
                        <Button onClick={handleAddCustomField}>Ajouter</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
